import { Centrifuge } from 'centrifuge'
import { dbgEvent } from './debug'
import { attemptTokenRefresh } from './trpc'
import { useAuthStore } from '../stores/auth.store'
import { useConnectionStore } from '../stores/connection.store'
import { useTerminalStore } from '../stores/terminal.store'

/**
 * Transport temps réel client — wrapper Centrifuge (swap étape 1b, Pulsar / MB Client).
 *
 * Remplace l'ancien client WebSocket brut (`/ws`). Côté client on coupe FRANCHEMENT le
 * WS brut : plus de native WebSocket, plus de heartbeat 25s, plus de backoff/scheduleReconnect
 * maison, plus de lastEventId/sync scalaire. Centrifuge gère lui-même reconnexion, ping et
 * recovery (offset+epoch par souscription).
 *
 * Rôle de ce module :
 *  1. Instancier UN client Centrifuge (singleton), sur l'endpoint WebSocket Centrifugo.
 *  2. Fournir `getToken` : récupère un JWT de connexion Centrifugo via GET /centrifugo/token,
 *     authentifié par l'access token MB App (lu à CHAQUE appel dans le store — jamais figé).
 *  3. Mapper les états de connexion Centrifuge → useConnectionStore.
 *  4. Exposer le client aux souscriptions (subscriptions.ts), qui portent la table de routage
 *     event→handler (contrat #10993).
 *
 * Export `wsClient` : nom conservé (façade), consommé par providers.tsx et subscriptions.ts.
 */

type ConnectionStatus = 'online' | 'reconnecting' | 'offline'

/**
 * Endpoint WebSocket Centrifugo.
 * - Prod : nginx proxifie `/connection/websocket` vers Centrifugo (:8000).
 * - Dev  : proxy Vite `/connection/websocket` (ws:true) vers :8000.
 * Dans les deux cas, même origine que l'app → on dérive host + protocole du document.
 */
function centrifugoEndpoint(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/connection/websocket`
}

/**
 * Récupère un token de connexion Centrifugo auprès du serveur MB App.
 * Auth par l'access token MB App (Bearer), lu à chaque appel — un token rafraîchi
 * (re-login) est donc pris en compte au prochain (re)connect Centrifuge. Throw si
 * l'access token est absent ou si le serveur répond en erreur (dont 503 quand le
 * secret HMAC Centrifugo n'est pas configuré) : Centrifuge émet alors un event
 * 'error' et retente selon sa politique interne.
 *
 * Sur 401, l'access token est rafraîchi puis la requête est rejouée UNE fois —
 * même contrat que le client tRPC (`createTrpcClient`) et que `mintEnrollment`.
 *
 * Pourquoi ce rattrapage est structurel et non défensif : l'access token MB App vit
 * 15 min, le token de connexion Centrifugo vit 1 h et n'est redemandé qu'au
 * (re)connect. Deux horloges différentes, donc un (re)connect qui tombe après
 * l'expiration de l'access token est un cas NOMINAL, pas un accident. Sans refresh
 * ici, ce cas nominal produisait une boucle morte : Centrifuge redemandait un token
 * toutes les quelques secondes, le serveur répondait 401, personne ne rafraîchissait,
 * et le temps réel ne revenait JAMAIS — l'app restait muette alors que le back
 * travaillait (incident du 2026-08-08 : ~300 GET /centrifugo/token en 401 sur 30 min).
 * Le seul déblocage possible était fortuit : qu'un appel tRPC rafraîchisse le token
 * juste avant un retry.
 *
 * Le refresh est mutualisé (promesse inflight singleton dans `trpc.ts`) : des retries
 * Centrifuge et des requêtes tRPC concurrents ne déclenchent qu'UN refresh.
 *
 * Un refresh en échec ne déconnecte pas l'utilisateur ici : on throw et Centrifuge
 * retente. La déconnexion (clearAuth + redirect login) reste portée par le client
 * tRPC, seul endroit où elle est déclenchée par une action utilisateur — un incident
 * réseau passager sur un retry de fond ne doit pas éjecter une session valide.
 */
async function fetchConnectionToken(): Promise<string> {
  const accessToken = useAuthStore.getState().accessToken
  if (!accessToken) {
    throw new Error('[centrifuge] no access token — cannot mint connection token')
  }
  let res = await fetch('/centrifugo/token', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (res.status === 401) {
    dbgEvent('WS', 'centrifuge token 401 — refreshing access token')
    const refreshed = await attemptTokenRefresh()
    if (!refreshed) {
      throw new Error('[centrifuge] access token refresh failed — cannot mint connection token')
    }
    const refreshedToken = useAuthStore.getState().accessToken
    if (!refreshedToken) {
      throw new Error('[centrifuge] no access token after refresh')
    }
    res = await fetch('/centrifugo/token', {
      headers: { Authorization: `Bearer ${refreshedToken}` },
    })
  }

  if (!res.ok) {
    throw new Error(`[centrifuge] token endpoint HTTP ${res.status}`)
  }
  const body = (await res.json()) as { token?: string }
  if (!body.token) {
    throw new Error('[centrifuge] token endpoint returned no token')
  }
  return body.token
}

class CentrifugeClient {
  private client: Centrifuge | null = null

  /**
   * Crée (une seule fois) l'instance Centrifuge et câble les états de connexion.
   * Idempotent : les appels suivants renvoient l'instance existante.
   */
  private ensureClient(): Centrifuge {
    if (this.client) return this.client

    const client = new Centrifuge(centrifugoEndpoint(), {
      getToken: () => fetchConnectionToken(),
    })

    client.on('connecting', (ctx) => {
      dbgEvent('WS', `centrifuge connecting (${ctx.code})`, { reason: ctx.reason })
      this.setStatus('reconnecting')

      // Invalide le jeton de connexion en cache à CHAQUE entrée en 'connecting' (1er
      // connect, où this._token est déjà vide donc no-op — ou reconnexion après coupure
      // de transport). Sans ce geste, centrifuge-js réutilise tel quel `this._token` sur
      // la 1re tentative de reconnexion (`_startReconnecting`, centrifuge/dist/centrifuge.js) :
      // si la coupure (réseau, veille machine, onglet en arrière-plan) a dépassé le TTL du
      // jeton (1 h, server/src/lib/centrifugo.ts CONNECTION_TOKEN_TTL_SECONDS), Centrifugo
      // rejette avec le code protocole 109 "token expired" — c'est exactement ce qui produit
      // le bandeau "Connexion temps réel perturbée (connect) : token expired"
      // (debug-centrifugo-connect-token-expired-2026-08-12).
      //
      // setToken('') force la branche getToken() de _startReconnecting dès la 1re tentative :
      // fetchConnectionToken() ci-dessus gère déjà un access token MB App périmé (retry sur
      // 401), donc un jeton Centrifugo frais est minté avant même d'essayer — l'erreur 109 ne
      // se produit plus. Pas de geste dans le handler 'disconnected' : cet event ne se
      // déclenche QUE sur une déconnexion terminale (notre disconnect() manuel, ou un code de
      // fermeture non-reconnectable côté serveur), jamais sur une coupure réseau/veille
      // ordinaire — celle-ci émet 'connecting' (_disconnect appelé avec reconnect=true, cf.
      // _waitServerPing et le onClose du transport dans centrifuge.js). Vérifié dans le code
      // de la librairie installée, pas déduit.
      client.setToken('')
    })
    client.on('connected', (ctx) => {
      dbgEvent('WS', 'centrifuge connected', { transport: ctx.transport })
      this.setStatus('online')
    })
    client.on('disconnected', (ctx) => {
      dbgEvent('WS', `centrifuge disconnected (${ctx.code})`, { reason: ctx.reason })
      this.setStatus('offline')
    })
    client.on('error', (ctx) => {
      console.error('[centrifuge] client error', ctx.type, ctx.error)

      // Trou #26 (audit-taxonomie-erreurs-terminal-pulsar-20260811) : cette erreur de transport
      // (ex. secret HMAC serveur non configuré → 503, refresh token en échec définitif) restait
      // console.error seul, jamais relayée au TERM. Elle est globale au client Centrifuge (pas de
      // sessionId associé — ErrorContext n'en porte pas), donc relayée à TOUTES les sessions
      // actuellement trackées par le terminal store, via le mécanisme appendClientError déjà
      // utilisé par messages.handler.ts et status.handler.ts (pas de nouveau store ni de bandeau
      // global à concevoir).
      const message = `Connexion temps réel perturbée (${ctx.type}) : ${ctx.error.message}`
      const sessionIds = Object.keys(useTerminalStore.getState().sessions)
      for (const sessionId of sessionIds) {
        useTerminalStore.getState().appendClientError(sessionId, message)
      }
    })

    this.client = client
    return client
  }

  /** Instance Centrifuge pour la gestion des souscriptions (subscriptions.ts). */
  getClient(): Centrifuge {
    return this.ensureClient()
  }

  /** Ouvre la connexion Centrifugo. Idempotent côté Centrifuge. */
  connect(): void {
    this.ensureClient().connect()
  }

  /** Ferme la connexion Centrifugo. */
  disconnect(): void {
    this.client?.disconnect()
  }

  private setStatus(status: ConnectionStatus): void {
    dbgEvent('Bus', 'emit connection:status', { status })
    useConnectionStore.getState().setStatus(status)
  }
}

export const wsClient = new CentrifugeClient()
